import * as React from 'react';
import { Checkbox, CircularProgress, FormControlLabel, TextField } from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import _ from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { fetchCustomers } from 'services/clients/customers';
import pluralize from 'pluralize';
import { FilterButton } from './FilterButton';

interface Props {
  customers: (string | null)[];
  setCustomers: (customers: (string | null)[]) => void;
}

export const CustomerFilter: React.FC<Props> = (props) => {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<string>();
  const { customers, setCustomers } = props;

  const customerQuery = useQuery(
    ['customers', inputValue],
    ({ signal }) => fetchCustomers({ signal, q: inputValue }),
    { keepPreviousData: true, enabled: open }
  );

  const handleClearFilter = () => {
    setCustomers([]);
  };

  const handleOnChangeCheckbox = (e: React.ChangeEvent<HTMLInputElement>, code: string | null) => {
    if (e.target.checked) {
      setCustomers([...new Set([...customers, code as string])]);
    } else {
      setCustomers(_.pull(customers, code));
    }
  };

  const { filteredCustomers, delta } = React.useMemo(() => {
    if (!customerQuery.data) {
      return { filteredCustomers: [], delta: 0 };
    }
    const filtered = customerQuery.data.filter((c) => c.count > 0);
    const difference = customerQuery.data.length - filtered.length;
    return { filteredCustomers: filtered, delta: difference };
  }, [customerQuery.data]);

  const disabled = !!customerQuery.data && filteredCustomers.length === 0;

  return (
    <FilterButton
      title="Customer ID"
      count={customers.length}
      clearFilter={handleClearFilter}
      isOpen={open}
      onRequestOpen={setOpen}
      placement="bottom-start"
      disabled={disabled}
    >
      <div className="flex flex-col px-2">
        <TextField
          autoFocus
          variant="outlined"
          size="small"
          placeholder="Filter by customer ID"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
          value={inputValue}
          InputProps={{
            endAdornment: (
              <div className="flex">
                {customerQuery.isFetching && <CircularProgress color="primary" size={12} />}
                {inputValue && (
                  <CloseIcon
                    className="cursor-pointer"
                    style={{ fontSize: 14 }}
                    onClick={() => setInputValue('')}
                  />
                )}
              </div>
            ),
          }}
        />
        <div className="flex flex-col max-h-96 overflow-y-auto scrollbar mt-2">
          {customerQuery.data && filteredCustomers.length === 0 && (
            <h4 className="font-medium text-red-900 ml-2 py-2">No customer found…</h4>
          )}
          {!inputValue && (
            <FormControlLabel
              classes={{
                label: 'font-medium text-sm',
                root: 'm-0 py-1 hover:bg-blue-200 rounded-sm',
              }}
              control={
                <Checkbox
                  size="small"
                  checked={customers.includes(null)}
                  onChange={(e) => {
                    handleOnChangeCheckbox(e, null);
                  }}
                />
              }
              label={<span className="font-bold text-gray-600">Blank</span>}
            />
          )}
          {customerQuery.data &&
            filteredCustomers.map((c) => (
              <FormControlLabel
                classes={{
                  label: 'font-medium text-sm',
                  root: 'm-0 py-1 hover:bg-blue-200 rounded-sm',
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={customers.includes(c.code)}
                    onChange={(e) => handleOnChangeCheckbox(e, c.code)}
                  />
                }
                label={c.code}
                key={c.id}
              />
            ))}
          {customerQuery.data && delta !== 0 && (
            <h5 className="ml-2">
              {delta} {pluralize('customer', delta)} not shown due to not being assigned to any
              containers
            </h5>
          )}
        </div>
      </div>
    </FilterButton>
  );
};
CustomerFilter.displayName = 'TrackAndTrace.Controls.filters.CustomerFilter';
